import { Dialog, DialogActions, DialogContent, DialogTitle } from 'components/Dialog';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Button } from '@mui/material';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import React, { useEffect, useId } from 'react';
import { usePolls } from 'features/polls/usePolls';
import { usePollTemplates } from 'features/polls/hooks/usePollTemplates';
import PollForm, { PollFormValues } from 'features/polls/components/PollForm';
import { selectPollTemplateById } from 'features/polls/pollsSlice';
import { editPollTemplateRequested } from 'features/polls/actions';

const EditPollTemplateModal = () => {
  const { t } = useTranslation(['modals', 'polls']);
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const templateId = modalData.id as string;

  const { isFeatureAvailable } = usePolls();
  const { canManageTemplates } = usePollTemplates();

  const template = useAppSelector((state) => selectPollTemplateById(state, templateId));

  const methods = useForm<PollFormValues>({
    mode: 'onChange',
    defaultValues: {
      question: template?.question || '',
      type: template?.type || 'single',
      choices: template?.choices || [],
      anonymous: template?.anonymous || false,
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const modalShouldBeClosed = !isFeatureAvailable || !canManageTemplates || !template;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('editPollTemplate'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const editPollTemplate: SubmitHandler<PollFormValues> = (data) => {
    dispatch(
      editPollTemplateRequested({
        id: templateId,
        question: data.question,
        type: data.type,
        choices: data.type === 'free' ? [] : data.choices.map((choice) => choice.text),
        anonymous: data.anonymous,
      })
    );
    dispatch(closeModal('editPollTemplate'));
  };

  const handleCancel = () => {
    dispatch(closeModal('editPollTemplate'));
  };

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      onClose={handleCancel}
      disableRestoreFocus
    >
      <DialogTitle id={`${modalId}-title`}>{t('polls.edit_poll_template.title')}</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <PollForm />
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button type="button" color="grayscale" onClick={handleCancel}>
          {t('polls.edit_poll_template.btn_cancel_label')}
        </Button>
        <Button type="button" onClick={handleSubmit(editPollTemplate)} disabled={!isValid}>
          {t('polls.edit_poll_template.btn_ok_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPollTemplateModal;
